import { useEffect, useState, useCallback, useRef } from "react";

export const useStepTimer = (isOpen: boolean, activeStep: number) => {
  const [timeInSeconds, setTimeInSeconds] = useState<number>(0);
  const startTimeRef = useRef<number>(Date.now());

  // Start timer without triggering re-renders
  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
  }, []);

  // Stop timer without updating state directly, avoiding re-renders
  const stopTimer = useCallback(() => {
    const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
    setTimeInSeconds(timeSpent); // Update only when necessary
    return timeSpent;
  }, []);

  // Visibility change handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stopTimer();
      } else {
        startTimer();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [startTimer, stopTimer]);

  // Control start/stop timer on modal open/close
  useEffect(() => {
    if (isOpen) {
      startTimer();
    } else {
      stopTimer();
    }
  }, [isOpen, startTimer, stopTimer]);

  // Track changes to activeStep only when the modal is open
  useEffect(() => {
    if (isOpen) {
      stopTimer();
      startTimer();
    }
  }, [activeStep, isOpen, startTimer, stopTimer]);

  return { timeInSeconds, stopTimer, startTimer };
};
